import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, X } from 'lucide-react';
import { apiPost } from '../../lib/api.js';
import { defaultPlacement } from '../../lib/teamHierarchy.js';

const FIELD = 'w-full bg-civix-panelLight border border-civix-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-hidden focus:border-civix-purple';

/**
 * "Add Team Member". With both permissions the caller picks who the new agent
 * reports to ("Place under me" / "Place under <supervisor>"); otherwise it is fixed.
 * @param {{open: boolean, onClose: () => void, onInvited: () => void, options?: Array<{value: string, label: string}>}} props
 * @returns {JSX.Element|null}
 */
export default function AddMemberModal({ open, onClose, onInvited, options = [] }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [placeUnder, setPlaceUnder] = useState('');
  const placement = placeUnder || defaultPlacement(options);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!open) return null;

  const close = () => { setForm({ name: '', email: '', phone: '' }); setResult(null); onClose(); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiPost('/team/invite', { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), placeUnder: placement });
      setResult(r);
      onInvited();
      toast.success(r.emailSent ? 'Invitation emailed' : 'Invite created — share the link');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not send the invite');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div role="dialog" aria-modal="true" aria-label="Add Team Member" className="w-full max-w-md rounded-xl bg-civix-panel border border-civix-border shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Add Team Member</h3>
          <button type="button" onClick={close} aria-label="Close" className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-white/70">{result.emailSent ? 'We emailed the invitation.' : 'Email could not be sent — share this link instead.'}</p>
            <div className="flex gap-2">
              <input readOnly value={result.inviteLink || result.link} className={`${FIELD} font-mono text-xs`} aria-label="Invite link" />
              <button type="button" onClick={() => { navigator.clipboard?.writeText(result.inviteLink || result.link); toast.success('Link copied'); }} className="px-3 rounded-lg bg-civix-purple text-white" aria-label="Copy link"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-white/45">They finish registration and verification from the link, then join the team you chose.</p>
            <button type="button" onClick={close} className="w-full py-2.5 rounded-md bg-civix-panelLight text-sm font-semibold">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input required value={form.name} onChange={set('name')} placeholder="Full name" aria-label="Full name" className={FIELD} />
            <input required type="email" value={form.email} onChange={set('email')} placeholder="Email address" aria-label="Email address" className={FIELD} />
            <input type="tel" value={form.phone} onChange={set('phone')} placeholder="Phone / seat (optional)" aria-label="Phone" className={FIELD} />
            {options.length > 1 && (
              <label className="block text-xs text-white/60 space-y-1">
                <span>Who will be their manager?</span>
                <select value={placement} onChange={(e) => setPlaceUnder(e.target.value)} aria-label="Place new agent under" data-testid="place-under" className={FIELD}>
                  {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            )}
            <button type="submit" disabled={busy} className="w-full py-2.5 rounded-md bg-civix-purple text-white text-sm font-bold hover:bg-civix-purpleDeep disabled:opacity-50">
              {busy ? 'Sending…' : 'Send Invitation'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
